import React, { useState, useEffect, useRef, useMemo } from "react";
import confetti from "canvas-confetti";
import {
  CheckCircle, Clock, User, Plus, Search, Users, ClipboardList,
  AlertCircle, Edit2, Trash2, Calendar, FileText, X,
  ListFilter, LayoutGrid, LayoutList, Tag, MapPin,
  AlertTriangle, Sparkles, CheckCheck
} from "lucide-react";
import { http } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import ResponsiveModal from "../ui/ResponsiveModal";

// ─── Constantes ────────────────────────────────────────────────
const PRIORIDAD_DIAS = 2; // Días sin resolver = atrasado

// ─── Helpers ───────────────────────────────────────────────────
function diasDesde(fecha) {
  if (!fecha) return 0;
  try {
    const d = new Date(fecha);
    if (isNaN(d.getTime())) return 0;
    return (Date.now() - d.getTime()) / 864e5;
  } catch {
    return 0;
  }
}

function fechaCorta(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-PY", { day: "2-digit", month: "short" });
  } catch {
    return iso.slice(0, 10);
  }
}

function fechaLarga(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("es-PY", { day: "2-digit", month: "short", year: "numeric" });
  } catch {
    return iso.slice(0, 10);
  }
}

// ─── Sub-componentes ───────────────────────────────────────────
function StatCard({ icon, label, value, color, pulse }) {
  const colors = {
    blue:    { bg: "bg-emerald-50",    text: "text-emerald-700",    iconBg: "bg-emerald-100 text-emerald-600" },
    amber:   { bg: "bg-amber-50",   text: "text-amber-700",   iconBg: "bg-amber-100 text-amber-600" },
    emerald: { bg: "bg-emerald-50", text: "text-emerald-700", iconBg: "bg-emerald-100 text-emerald-600" },
    red:     { bg: "bg-red-50",     text: "text-red-700",     iconBg: "bg-red-100 text-red-600" },
  };
  const c = colors[color] || colors.blue;
  return (
    <div className={`${c.bg} rounded-xl p-3 sm:p-4 border border-white/60`}>
      <div className="flex items-center gap-2 mb-1">
        <div className={`p-1.5 rounded-lg ${c.iconBg}`}>{icon}</div>
        <span className="text-xs font-medium text-gray-500 truncate">{label}</span>
      </div>
      <p className={`text-xl sm:text-2xl font-bold ${c.text} ${pulse ? "animate-pulse" : ""}`}>{value}</p>
    </div>
  );
}

function TabButton({ active, onClick, icon, label, count, color }) {
  const activeColors = { amber: "border-amber-500 text-amber-700", emerald: "border-emerald-500 text-emerald-700" };
  const badgeColors  = { amber: "bg-amber-100 text-amber-700",     emerald: "bg-emerald-100 text-emerald-700" };
  return (
    <button
      onClick={onClick}
      className={`flex-1 flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-all
        ${active ? `${activeColors[color]} bg-white` : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"}`}
    >
      {icon}
      <span>{label}</span>
      <span className={`text-xs px-1.5 py-0.5 rounded-full font-semibold ${active ? badgeColors[color] : "bg-gray-100 text-gray-500"}`}>{count}</span>
    </button>
  );
}

function EmptyState({ tab, filtro }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="p-4 bg-gray-100 rounded-2xl mb-4">
        {tab === "pendiente" ? <Sparkles className="w-10 h-10 text-gray-400" /> : <CheckCircle className="w-10 h-10 text-gray-400" />}
      </div>
      <h3 className="text-lg font-semibold text-gray-700 mb-1">
        {filtro ? "Sin resultados" : tab === "pendiente" ? "¡Todo al día!" : "Sin completados aún"}
      </h3>
      <p className="text-sm text-gray-500 max-w-xs">
        {filtro ? `No se encontraron pendientes para "${filtro}".`
          : tab === "pendiente" ? "No hay tareas pendientes. Crea una nueva para empezar."
          : "Cuando completes pendientes aparecerán aquí."}
      </p>
    </div>
  );
}

function PendienteCard({ p, expanded, onToggleExpand, onCheck, onEdit, onDelete }) {
  const atrasado = p.estado === "pendiente" && diasDesde(p.fecha_creacion) > PRIORIDAD_DIAS;
  const diasPendiente = Math.floor(diasDesde(p.fecha_creacion));
  const isCompleted = p.estado === "completado";
  return (
    <div className={`group rounded-xl border transition-all duration-200
      ${isCompleted ? "bg-emerald-50/40 border-emerald-200 hover:border-emerald-300"
        : atrasado ? "bg-red-50/30 border-red-200 hover:border-red-300 ring-1 ring-red-100"
        : "bg-white border-gray-200 hover:border-emerald-300 hover:shadow-md"}`}>
      {atrasado && <div className="h-1 bg-gradient-to-r from-red-400 to-orange-400 rounded-t-xl" />}
      {isCompleted && <div className="h-1 bg-gradient-to-r from-emerald-400 to-teal-400 rounded-t-xl" />}
      <div className="p-4">
        {/* Header */}
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1 min-w-0">
            <h3 className={`font-semibold text-sm sm:text-base leading-snug break-words ${isCompleted ? "text-emerald-800 line-through decoration-emerald-300" : "text-gray-900"}`}>{p.titulo}</h3>
            <div className="flex flex-wrap items-center gap-1.5 mt-2">
              {atrasado && (
                <span className="inline-flex items-center gap-1 text-[11px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-700">
                  <AlertTriangle className="w-3 h-3" />{diasPendiente}d atrasado
                </span>
              )}
              {p.fecha_estimada && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full ${atrasado ? "bg-red-50 text-red-600" : "bg-emerald-50 text-emerald-600"}`}>
                  <Calendar className="w-3 h-3" />{fechaCorta(p.fecha_estimada)}
                </span>
              )}
              {p.tipo && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600">
                  <Tag className="w-3 h-3" />{p.tipo}
                </span>
              )}
            </div>
          </div>
          {!isCompleted && (
            <div className="flex items-center gap-0.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
              <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors" title="Editar"><Edit2 className="w-3.5 h-3.5" /></button>
              <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Eliminar"><Trash2 className="w-3.5 h-3.5" /></button>
            </div>
          )}
        </div>
        {/* Descripción */}
        {p.descripcion && (
          <div className="mb-3">
            <p className={`text-sm text-gray-600 leading-relaxed whitespace-pre-line ${expanded ? "" : "line-clamp-2"}`}>{p.descripcion}</p>
            {p.descripcion.length > 100 && (
              <button onClick={onToggleExpand} className="text-xs text-emerald-500 hover:text-emerald-700 mt-1 font-medium">{expanded ? "Ver menos" : "Ver más..."}</button>
            )}
          </div>
        )}
        {/* Chips */}
        <div className="flex flex-wrap items-center gap-1.5 text-[11px] text-gray-500 mb-3">
          <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-full border border-gray-100"><User className="w-3 h-3 text-gray-400" />{p.usuario_asignado}</span>
          {p.cliente && <span className="inline-flex items-center gap-1 bg-gray-50 px-2 py-1 rounded-full border border-gray-100"><Users className="w-3 h-3 text-gray-400" />{p.cliente}</span>}
        </div>
        {/* Acción */}
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <label className="flex items-center gap-2.5 cursor-pointer select-none group/check">
            <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isCompleted ? "bg-emerald-500 border-emerald-500 scale-105" : "border-gray-300 group-hover/check:border-emerald-400 group-hover/check:bg-emerald-50"}`}>
              {isCompleted && <CheckCircle className="w-3.5 h-3.5 text-white" />}
            </div>
            <input type="checkbox" checked={isCompleted} onChange={e => onCheck(e.target.checked)} className="hidden" />
            <span className={`text-sm font-medium transition-colors ${isCompleted ? "text-emerald-700" : "text-gray-500 group-hover/check:text-emerald-600"}`}>{isCompleted ? "Completado" : "Marcar hecho"}</span>
          </label>
          {isCompleted && p.fecha_finalizacion && <span className="text-[10px] text-gray-400 flex items-center gap-1"><Clock className="w-3 h-3" />{fechaLarga(p.fecha_finalizacion)}</span>}
          {!isCompleted && p.fecha_creacion && <span className="text-[10px] text-gray-400 flex items-center gap-1" title="Fecha de creación"><Clock className="w-3 h-3" />Creado {fechaCorta(p.fecha_creacion)}</span>}
        </div>
      </div>
    </div>
  );
}

function PendienteRow({ p, onCheck, onEdit, onDelete }) {
  const atrasado = p.estado === "pendiente" && diasDesde(p.fecha_creacion) > PRIORIDAD_DIAS;
  const isCompleted = p.estado === "completado";
  return (
    <div className={`group flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-3 rounded-xl border transition-all
      ${isCompleted ? "bg-emerald-50/40 border-emerald-200"
        : atrasado ? "bg-red-50/30 border-red-200"
        : "bg-white border-gray-200 hover:border-emerald-200 hover:shadow-sm"}`}>
      <label className="cursor-pointer shrink-0">
        <div className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all ${isCompleted ? "bg-emerald-500 border-emerald-500" : "border-gray-300 hover:border-emerald-400 hover:bg-emerald-50"}`}>
          {isCompleted && <CheckCircle className="w-3.5 h-3.5 text-white" />}
        </div>
        <input type="checkbox" checked={isCompleted} onChange={e => onCheck(e.target.checked)} className="hidden" />
      </label>
      {atrasado && <div className="w-1 self-stretch rounded-full bg-red-400 shrink-0" />}
      <div className="flex-1 min-w-0">
        <p className={`text-sm font-medium truncate ${isCompleted ? "text-emerald-700 line-through" : "text-gray-900"}`}>{p.titulo}</p>
        <div className="flex flex-wrap items-center gap-1.5 mt-1">
          <span className="text-[11px] text-gray-400 flex items-center gap-1"><User className="w-3 h-3" /> {p.usuario_asignado}</span>
          {p.cliente && <span className="text-[11px] text-gray-400 flex items-center gap-1"><Users className="w-3 h-3" /> {p.cliente}</span>}
          {p.fecha_estimada && <span className={`text-[11px] flex items-center gap-1 ${atrasado ? "text-red-500 font-medium" : "text-gray-400"}`}><Calendar className="w-3 h-3" /> {fechaCorta(p.fecha_estimada)}</span>}
          {atrasado && <span className="text-[11px] font-semibold text-red-600 flex items-center gap-1"><AlertTriangle className="w-3 h-3" /> Atrasado</span>}
        </div>
      </div>
      {!isCompleted && (
        <div className="flex items-center gap-0.5 shrink-0 opacity-100 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
          <button onClick={onEdit} className="p-1.5 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"><Edit2 className="w-3.5 h-3.5" /></button>
          <button onClick={onDelete} className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"><Trash2 className="w-3.5 h-3.5" /></button>
        </div>
      )}
    </div>
  );
}

// ─── Componente principal ──────────────────────────────────────
export default function Pendientes() {
  const { user } = useAuth();

  // Data
  const [pendientes, setPendientes] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);

  // UI
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [tab, setTab] = useState("pendiente");
  const [filtro, setFiltro] = useState("");
  const [vistaGrid, setVistaGrid] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [filtroUsuario, setFiltroUsuario] = useState("");
  const [expandedId, setExpandedId] = useState(null);

  // Celebración
  const [showCelebration, setShowCelebration] = useState(false);
  const audioRef = useRef(null);
  const canvasRef = useRef(null);
  const confettiRef = useRef(null);

  // Form
  const emptyForm = { titulo: "", descripcion: "", tipo: "", usuario_asignado: "", fecha_estimada: "", cliente: "", sucursal: "", estado: "pendiente", generar_agendamiento: false };
  const [nuevo, setNuevo] = useState(emptyForm);

  // Sucursales del cliente actualmente seleccionado
  const clienteSeleccionado = clientes.find(c => c.nombre === nuevo.cliente);
  const sucursalesCliente = clienteSeleccionado?.sucursales || [];
  const tieneSucursales = sucursalesCliente.length > 0;

  // ── Confetti canvas ──
  useEffect(() => {
    const canvas = document.createElement("canvas");
    Object.assign(canvas.style, { position: "fixed", top: 0, left: 0, width: "100%", height: "100%", pointerEvents: "none", zIndex: 99999 });
    document.body.appendChild(canvas);
    canvasRef.current = canvas;
    confettiRef.current = confetti.create(canvas, { resize: true, useWorker: false });
    return () => canvas.parentNode?.removeChild(canvas);
  }, []);

  // ── Carga inicial ──
  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    setLoading(true);
    try {
      const [resPend, resUsers, resClients] = await Promise.allSettled([
        http.get("/pendientes"), http.get("/usuarios"), http.get("/clientes"),
      ]);
      setPendientes(resPend.status === "fulfilled" ? resPend.value.data || [] : []);
      if (resUsers.status === "fulfilled") {
        setUsuarios(Array.isArray(resUsers.value.data) ? resUsers.value.data : []);
      } else if (user) {
        setUsuarios([{ username: user.username, nombre_completo: user.nombre_completo || user.username }]);
      }
      setClientes(resClients.status === "fulfilled" && Array.isArray(resClients.value.data) ? resClients.value.data : []);
    } catch (err) {
      console.error("Error cargando datos:", err);
    } finally {
      setLoading(false);
    }
  };

  // ── Estadísticas y filtrado ──
  const stats = useMemo(() => {
    const pend = pendientes.filter(p => p.estado === "pendiente").length;
    const comp = pendientes.filter(p => p.estado === "completado").length;
    const atrasados = pendientes.filter(p => p.estado === "pendiente" && diasDesde(p.fecha_creacion) > PRIORIDAD_DIAS).length;
    return { todos: pendientes.length, pend, comp, atrasados };
  }, [pendientes]);

  const pendientesFiltrados = useMemo(() => {
    const busqueda = filtro.toLowerCase();
    return pendientes
      .filter(p => p.estado === tab)
      .filter(p => !busqueda || [p.titulo, p.descripcion, p.tipo, p.usuario_asignado, p.cliente].some(f => f?.toLowerCase().includes(busqueda)))
      .filter(p => !filtroUsuario || p.usuario_asignado === filtroUsuario)
      .sort((a, b) => {
        if (tab === "pendiente") {
          const aA = diasDesde(a.fecha_creacion) > PRIORIDAD_DIAS;
          const bA = diasDesde(b.fecha_creacion) > PRIORIDAD_DIAS;
          if (aA && !bA) return -1;
          if (!aA && bA) return 1;
        }
        return new Date(b.fecha_creacion || 0).getTime() - new Date(a.fecha_creacion || 0).getTime();
      });
  }, [pendientes, tab, filtro, filtroUsuario]);

  // ── CRUD ──
  const crearPendiente = async (e) => {
    e.preventDefault();
    try {
      if (editando) { await http.put(`/pendientes/${editando}`, nuevo); setEditando(null); }
      else { await http.post("/pendientes", nuevo); }
      setNuevo(emptyForm);
      setDrawerOpen(false);
      cargarDatos();
    } catch (error) {
      console.error("Error al guardar pendiente:", error);
      alert("Error al guardar pendiente");
    }
  };

  const marcarHecho = async (id, checked) => {
    try {
      await http.put(`/pendientes/${id}`, { estado: checked ? "completado" : "pendiente" });
      if (checked && confettiRef.current) {
        const fire = (ratio, opts) => confettiRef.current({ origin: { y: 0.7 }, ...opts, particleCount: Math.floor(200 * ratio), disableForReducedMotion: true });
        fire(0.25, { spread: 26, startVelocity: 55 });
        fire(0.2, { spread: 60 });
        fire(0.35, { spread: 100, decay: 0.91, scalar: 0.8 });
        fire(0.1, { spread: 120, startVelocity: 25, decay: 0.92, scalar: 1.2 });
        fire(0.1, { spread: 120, startVelocity: 45 });
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 2500);
      }
      cargarDatos();
    } catch (error) {
      console.error("Error al marcar como hecho:", error);
      alert(`Error: ${error.response?.data?.detail || error.message}`);
    }
  };

  const editarPendiente = (p) => {
    setNuevo({ titulo: p.titulo || "", descripcion: p.descripcion || "", tipo: p.tipo || "", usuario_asignado: p.usuario_asignado || "", fecha_estimada: p.fecha_estimada ? p.fecha_estimada.split("T")[0] : "", cliente: p.cliente || "", sucursal: p.sucursal || "", estado: p.estado || "pendiente", generar_agendamiento: false });
    setEditando(p.id);
    setDrawerOpen(true);
  };

  const eliminarPendiente = async (id, titulo) => {
    if (!window.confirm(`¿Eliminar "${titulo}"?`)) return;
    try { await http.delete(`/pendientes/${id}`); cargarDatos(); }
    catch (error) { console.error("Error al eliminar pendiente:", error); alert("Error al eliminar pendiente"); }
  };

  const cancelarEdicion = () => { setEditando(null); setNuevo(emptyForm); setDrawerOpen(false); };

  // ── Render ──
  return (
    <>
      <div className="w-full max-w-6xl mx-auto px-3 sm:px-4 lg:px-6 py-4 sm:py-6">

        {/* ═══ HEADER ═══ */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center gap-2">
              <div className="p-2 bg-emerald-100 rounded-xl"><ClipboardList className="w-5 h-5 sm:w-6 sm:h-6 text-emerald-600" /></div>
              Pendientes
            </h1>
            <p className="text-sm text-gray-500 mt-0.5 hidden sm:block">Gestiona las tareas pendientes del equipo</p>
          </div>
          <button onClick={() => { setEditando(null); setNuevo(emptyForm); setDrawerOpen(true); }}
            className="flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl shadow-sm hover:bg-emerald-700 active:scale-[0.97] transition-all font-medium text-sm sm:text-base w-full sm:w-auto">
            <Plus className="w-4 h-4" /> Nuevo Pendiente
          </button>
        </div>

        {/* ═══ STATS ═══ */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-5">
          <StatCard icon={<ClipboardList className="w-4 h-4" />} label="Total" value={stats.todos} color="blue" />
          <StatCard icon={<Clock className="w-4 h-4" />} label="Pendientes" value={stats.pend} color="amber" />
          <StatCard icon={<CheckCheck className="w-4 h-4" />} label="Completados" value={stats.comp} color="emerald" />
          <StatCard icon={<AlertTriangle className="w-4 h-4" />} label="Atrasados" value={stats.atrasados} color="red" pulse={stats.atrasados > 0} />
        </div>

        {/* ═══ TABS + BÚSQUEDA ═══ */}
        <div className="bg-white rounded-xl border border-gray-200 shadow-sm mb-4 overflow-hidden">
          <div className="flex border-b border-gray-100">
            <TabButton active={tab === "pendiente"} onClick={() => setTab("pendiente")} icon={<Clock className="w-4 h-4" />} label="Pendientes" count={stats.pend} color="amber" />
            <TabButton active={tab === "completado"} onClick={() => setTab("completado")} icon={<CheckCircle className="w-4 h-4" />} label="Completados" count={stats.comp} color="emerald" />
          </div>
          <div className="p-3 flex flex-col sm:flex-row gap-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input type="text" className="w-full pl-9 pr-8 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" placeholder="Buscar pendientes..." value={filtro} onChange={e => setFiltro(e.target.value)} />
              {filtro && <button onClick={() => setFiltro("")} className="absolute right-2 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>}
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setShowFilters(!showFilters)} className={`flex items-center gap-1.5 px-3 py-2 rounded-lg border text-sm transition-colors ${showFilters || filtroUsuario ? "bg-emerald-50 border-emerald-200 text-emerald-700" : "bg-gray-50 border-gray-200 text-gray-600 hover:bg-gray-100"}`}>
                <ListFilter className="w-4 h-4" /><span className="hidden sm:inline">Filtros</span>{filtroUsuario && <span className="w-2 h-2 rounded-full bg-emerald-500" />}
              </button>
              <div className="hidden sm:flex items-center bg-gray-100 rounded-lg p-0.5 border border-gray-200">
                <button onClick={() => setVistaGrid(true)} className={`p-1.5 rounded-md transition-colors ${vistaGrid ? "bg-white shadow-sm text-emerald-600" : "text-gray-400 hover:text-gray-600"}`} title="Vista cuadrícula"><LayoutGrid className="w-4 h-4" /></button>
                <button onClick={() => setVistaGrid(false)} className={`p-1.5 rounded-md transition-colors ${!vistaGrid ? "bg-white shadow-sm text-emerald-600" : "text-gray-400 hover:text-gray-600"}`} title="Vista lista"><LayoutList className="w-4 h-4" /></button>
              </div>
            </div>
          </div>
          {showFilters && (
            <div className="px-3 pb-3 flex flex-wrap gap-2 border-t border-gray-100 pt-3">
              <select value={filtroUsuario} onChange={e => setFiltroUsuario(e.target.value)} className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm bg-white focus:ring-2 focus:ring-emerald-500/30">
                <option value="">Todos los usuarios</option>
                {usuarios.map(u => <option key={u._id || u.id || u.username} value={u.username}>{u.nombre_completo || u.nombre || u.username}</option>)}
              </select>
              {filtroUsuario && <button onClick={() => setFiltroUsuario("")} className="px-3 py-1.5 text-xs text-red-600 bg-red-50 rounded-lg border border-red-200 hover:bg-red-100 transition-colors flex items-center gap-1"><X className="w-3 h-3" /> Limpiar</button>}
            </div>
          )}
        </div>

        {/* ═══ LISTA ═══ */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-sm text-gray-500">Cargando pendientes...</p>
          </div>
        ) : pendientesFiltrados.length === 0 ? (
          <EmptyState tab={tab} filtro={filtro} />
        ) : (
          <div className={vistaGrid ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" : "flex flex-col gap-2"}>
            {pendientesFiltrados.map(p => vistaGrid ? (
              <PendienteCard key={p.id} p={p} expanded={expandedId === p.id} onToggleExpand={() => setExpandedId(expandedId === p.id ? null : p.id)} onCheck={checked => marcarHecho(p.id, checked)} onEdit={() => editarPendiente(p)} onDelete={() => eliminarPendiente(p.id, p.titulo)} />
            ) : (
              <PendienteRow key={p.id} p={p} onCheck={checked => marcarHecho(p.id, checked)} onEdit={() => editarPendiente(p)} onDelete={() => eliminarPendiente(p.id, p.titulo)} />
            ))}
          </div>
        )}

        {!loading && pendientesFiltrados.length > 0 && (
          <p className="text-center text-xs text-gray-400 mt-4">
            {pendientesFiltrados.length} {pendientesFiltrados.length === 1 ? "pendiente" : "pendientes"}{filtro && ` encontrados para "${filtro}"`}
          </p>
        )}
      </div>

      {/* ═══ MODAL CREAR / EDITAR ═══ */}
      <ResponsiveModal isOpen={drawerOpen} onClose={cancelarEdicion} title={editando ? "Editar Pendiente" : "Nuevo Pendiente"} size="default">
        <form onSubmit={crearPendiente} className="space-y-4 p-1">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Título *</label>
            <div className="relative">
              <FileText className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" placeholder="Ej: Revisar servidor del cliente..." value={nuevo.titulo} onChange={e => setNuevo({ ...nuevo, titulo: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tipo</label>
              <div className="relative">
                <Tag className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" placeholder="Ej: Redes, General..." value={nuevo.tipo} onChange={e => setNuevo({ ...nuevo, tipo: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Asignado a *</label>
              <div className="relative">
                <User className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" value={nuevo.usuario_asignado} onChange={e => setNuevo({ ...nuevo, usuario_asignado: e.target.value })} required>
                  <option value="">Seleccionar...</option>
                  {usuarios.map(u => <option key={u._id || u.id || u.username} value={u.username}>{u.nombre_completo || u.nombre || u.username}</option>)}
                </select>
              </div>
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Descripción</label>
            <textarea className="w-full px-3 py-2.5 border border-gray-300 rounded-lg text-sm min-h-[80px] resize-y focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" placeholder="Detalles adicionales..." value={nuevo.descripcion} onChange={e => setNuevo({ ...nuevo, descripcion: e.target.value })} />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Estimada</label>
              <div className="relative">
                <Calendar className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input type="date" className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" value={nuevo.fecha_estimada} onChange={e => setNuevo({ ...nuevo, fecha_estimada: e.target.value })} />
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Cliente</label>
              <div className="relative">
                <Users className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" value={nuevo.cliente} onChange={e => setNuevo({ ...nuevo, cliente: e.target.value, sucursal: "" })}>
                  <option value="">Ninguno</option>
                  {clientes.map(c => <option key={c._id || c.id} value={c.nombre}>{c.nombre}</option>)}
                </select>
              </div>
            </div>
          </div>
          {/* Sucursal - solo si el cliente tiene sucursales */}
          {tieneSucursales && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Sucursal</label>
              <div className="relative">
                <MapPin className="h-4 w-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <select className="w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-lg text-sm appearance-none bg-white focus:ring-2 focus:ring-emerald-500/30 focus:border-emerald-400 transition-colors" value={nuevo.sucursal} onChange={e => setNuevo({ ...nuevo, sucursal: e.target.value })}>
                  <option value="">Sede principal</option>
                  {sucursalesCliente.map((s, i) => (
                    <option key={s.nombre || i} value={s.nombre}>{s.nombre}{s.ubicacion_ciudad ? ` - ${s.ubicacion_ciudad}` : ""}</option>
                  ))}
                </select>
              </div>
            </div>
          )}
          {!editando && nuevo.cliente && nuevo.fecha_estimada && (
            <div className="bg-emerald-50/70 p-3 rounded-xl border border-emerald-100 flex items-start gap-3">
              <input type="checkbox" id="generar_agendamiento" className="mt-0.5 w-4 h-4 text-emerald-600 border-gray-300 rounded focus:ring-emerald-500" checked={nuevo.generar_agendamiento || false} onChange={e => setNuevo({ ...nuevo, generar_agendamiento: e.target.checked })} />
              <label htmlFor="generar_agendamiento" className="text-sm text-emerald-800 cursor-pointer leading-snug">
                <strong>Generar agendamiento automático</strong>
                <p className="text-xs text-emerald-600 mt-0.5">Se creará un agendamiento para el {nuevo.fecha_estimada} vinculado a este pendiente.</p>
              </label>
            </div>
          )}
          <div className="pt-3 border-t flex flex-col-reverse sm:flex-row justify-end gap-2">
            <button type="button" onClick={cancelarEdicion} className="px-4 py-2.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-xl hover:bg-gray-50 transition-colors w-full sm:w-auto">Cancelar</button>
            <button type="submit" className="px-5 py-2.5 text-sm font-medium text-white bg-emerald-600 rounded-xl shadow-sm hover:bg-emerald-700 active:scale-[0.97] transition-all flex items-center justify-center gap-2 w-full sm:w-auto">
              {editando ? <Edit2 className="w-4 h-4" /> : <Plus className="w-4 h-4" />}
              {editando ? "Actualizar" : "Crear Pendiente"}
            </button>
          </div>
        </form>
      </ResponsiveModal>

      {/* ═══ CELEBRACIÓN ═══ */}
      {showCelebration && (
        <div className="fixed inset-0 flex items-center justify-center z-50 pointer-events-none">
          <div className="flex flex-col items-center">
            <span className="text-7xl sm:text-8xl drop-shadow-lg animate-bounce">🎉</span>
            <span className="mt-3 text-2xl sm:text-3xl font-bold text-emerald-700 animate-pulse">¡Bien hecho!</span>
          </div>
        </div>
      )}
      <audio ref={audioRef} src="/assets/applause.mp3" preload="auto" />
    </>
  );
}
